/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */


import { ReportData } from '../../types/reportData';
import { getNaType } from '../../utils/universalTrackerValue';
import { NotApplicableTypes } from '../../constants/status';
import { TotalEmissionIntensityType } from './sgx-metric-report/data/GHGEmissionsIntensity';
import { TableColumnType } from '../../types/universalTracker';
import { VisibilityStatus } from '../../types/download';
import { formatCalculationNumber } from '../../utils/formula';

const EMISSION_INTENSITY_DEFAULT_DATA = {
  code: '',
  name: '',
  intensity: '',
  numericValue: 0,
};
export const defaultPrivateQuestionText = 'Company has marked this as Private';

function getNaText(naType: string) {
  switch (naType) {
    case NotApplicableTypes.nr:
      return 'Not reporting';
    case NotApplicableTypes.na:
      return 'Not Applicable';
    default:
      return '-';
  }
}

/**
 * Only works with a single row for now
 */
export const getReportUtrvValue = (
  reportData: ReportData[],
  utrCode: string,
  visibility: VisibilityStatus,
  tableColumn?: string,
  fallback?: string | number,
) => {
  const utrv = reportData.find(d => d.universalTracker.code === utrCode);

  if (!utrv) {
    return fallback
  }
  if (shouldDisplayPrivateText(utrv, visibility)) {
    return typeof fallback === 'number' ? fallback : defaultPrivateQuestionText;
  }
  const naType = getNaType(utrv);
  if (naType) {
    return typeof fallback === 'number' ? fallback : getNaText(naType)
  }

  if (tableColumn) {
    return utrv?.valueData?.table?.[0]?.find(col => col.code === tableColumn)?.value ?? fallback;
  }

  if (utrv.valueType === 'text' || utrv.valueType === 'textValueList') {
    return utrv.valueData?.data;
  }

  return utrv?.value ?? fallback;
}

interface UtrvDataLookup {
  reportData: ReportData[];
  utrCode: string,
  tableColumn?: string
  fallback?: string | number;
  visibility: VisibilityStatus;
}

interface UtrvDataResult {
  numericValue: number;
  value: string | number | undefined;
  isPrivate?: boolean;
  utrv?: ReportData
}


function getNumericValue(value: unknown, fallback: string | number | undefined): number {
  if (typeof value === 'number') {
    return value
  }
  return typeof fallback === 'number' ? fallback : 0;
}

export const getUtrvData = (data: UtrvDataLookup): UtrvDataResult => {

  const { visibility, reportData, utrCode, tableColumn, fallback } = data;

  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  if (!utrv) {
    return { numericValue: 0, value: fallback }
  }

  if (shouldDisplayPrivateText(utrv, visibility)) {
    return {
      numericValue: getNumericValue(undefined, fallback),
      value: defaultPrivateQuestionText,
      isPrivate: utrv.isPrivate,
      utrv,
    }
  }
  const naType = getNaType(utrv);
  if (naType) {
    return {
      numericValue: getNumericValue(undefined, fallback),
      value: getNaText(naType),
      utrv,
    }
  }

  if (tableColumn) {
    const value = utrv?.valueData?.table?.[0]?.find(col => col.code === tableColumn)?.value;
    if (Array.isArray(value)) {
      return {
        numericValue: 0,
        // Messed up, but we don't need this for now...
        value: value.join(', '),
        utrv,
      }
    }


    if (typeof value === 'string') {
      // Check if we need to convert to number
      const cols = utrv.universalTracker.valueValidation?.table?.columns ?? [];
      const column = cols.find(c => c.code === tableColumn)
      if (column && column.type === TableColumnType.Number) {
        try {
          const numericValue = parseFloat(value);
          return {
            numericValue: getNumericValue(numericValue, fallback),
            value: value,
            utrv,
          }
        } catch (e) {
          console.error(e)
        }
      }
    }


    return {
      numericValue: getNumericValue(value, fallback),
      value: value,
      utrv,
    }
  }

  if (utrv.valueType === 'text') {
    return {
      numericValue: getNumericValue(undefined, fallback),
      value: utrv.valueData?.data,
      utrv,
    }
  }

  return {
    numericValue: getNumericValue(utrv.value, fallback),
    value: utrv.value,
    utrv,
  }
}

export function shouldDisplayPrivateText(value: { isPrivate?: boolean }, visibility: VisibilityStatus) {
  return value.isPrivate && visibility !== VisibilityStatus.Include;
}

export const sumUtrvDataResults = (values: UtrvDataResult[], visibility: VisibilityStatus): string | number => {

  let result = 0;

  for (const value of values) {
    if (shouldDisplayPrivateText(value, visibility)) {
      return defaultPrivateQuestionText;
    }

    result += value.numericValue
  }
  return result
}

const getValueListName = (utrv: ReportData, valueListCode: string) => {
  const option = utrv.tableValueList?.map((valueList) => valueList?.options?.find(option => option.code === valueListCode));
  return option?.[0]?.name ?? valueListCode;
};

export const getSustainabilityData = (reportData: ReportData[], utrCode: string, visibility: VisibilityStatus) => {
  const utrv = reportData.find((d) => d.universalTracker.code === utrCode);

  if (!utrv) {
    return;
  }

  if (shouldDisplayPrivateText(utrv, visibility)) {
    return defaultPrivateQuestionText;
  }

  const naType = getNaType(utrv);
  if (naType) {
    return getNaText(naType);
  }

  return utrv.valueData?.table?.map((item) => {
    return item.reduce((accumulator, item) => ({
        [item.code]: getValueListName(utrv, item.value as string),
        ...accumulator,
      }),
      {}
    );
  });
};

export const getGovernanceData = (reportData: ReportData[], utrCode: string, visibility: VisibilityStatus) => {
  const utrv = reportData.find((d) => d.universalTracker.code === utrCode);

  if (!utrv) {
    return;
  }
  const naType = getNaType(utrv);
  const defaultText = shouldDisplayPrivateText(utrv, visibility) ? defaultPrivateQuestionText : naType ? getNaText(naType) : null;
  const governanceData = utrv?.universalTracker?.valueValidation?.table?.columns.map((question) => {
    const answer = utrv?.valueData?.table?.[0]?.find((answer) => answer.code === question.code);
    return { name: question?.name, value: defaultText ?? getValueListName(utrv, answer?.value as string) };
  });
  return governanceData;
};

/** Way too complicated... **/
export const getEmissionIntensityData = (
  reportData: ReportData[],
  utrCode: string,
  currentTotals: TotalEmissionIntensityType[],
  visibility: VisibilityStatus
): TotalEmissionIntensityType => {
  const utrv = reportData.find((d) => d.universalTracker.code === utrCode);

  if (!utrv || !utrv.valueData?.table) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  if (shouldDisplayPrivateText(utrv, visibility)) {
    return {
      code: '',
      name: defaultPrivateQuestionText,
      intensity: defaultPrivateQuestionText,
      numericValue: 0,
    }
  }

  const [firstRow] = utrv.valueData.table;
  if (!firstRow) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  /**
   * Available column codes.
   * Denominator is a list of other column codes.
   * [
   *     "sgx56-total-emissions",
   *     "sgx56-denominator", // Determine which columns to show
   *
   *     "sgx56-rev",
   *     "sgx56-emissions-intens-rev", // sgx56-denominator1
   *
   *     "sgx56-headcount",
   *     "sgx56-emissions-intens-headcount", // sgx56-denominator2
   *
   *     "sgx56-floorarea", // sgx56-denominator3
   *     "sgx56-emissions-intens-floorarea",
   *
   *     "sgx56-other-org-metric", // sgx56-denominator4
   *     "sgx56-other-org-metric-amount",
   *     "sgx56-emissions-intens-othermetric"
   * ]
   */
  const initialCode = currentTotals.find((item) => item.code)?.code ?? '';
  const columns = utrv.universalTracker.valueValidation?.table?.columns;

  const table = utrv.valueData.table;
  const sgx56Denominator = 'sgx56-denominator';

  // Find a row that have the same code as previous one, or if we don't have one select first
  // as we have not yet determined our search column value like all questions by revenue or headcount etc.
  // @TODO: Technically there could be more rows with the same denominator, since we don't have unique validation.
  const tableRow = initialCode ? table.find((rows) => {
    return rows.find((column) => column.code === sgx56Denominator)?.value === initialCode;
  }): firstRow;

  // utrv table does not contain a row with the code we need, skip it
  if (!tableRow) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  // On the row for the right denominator, should have only one intensity calculated column
  const valueColumn = tableRow.find((col) => /emissions-intens/.test(col.code) && col.value !== undefined);
  if (!valueColumn) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  const tableColumn = columns?.find((c) => c.code === valueColumn.code);
  if (!tableColumn) {
    return EMISSION_INTENSITY_DEFAULT_DATA;
  }

  // Find the name for our denominator value
  const code = tableRow.find((col) => col.code === sgx56Denominator)?.value as string;
  const name = code === 'sgx56-denominator4'
    ? (tableRow.find((col) => col.code === 'sgx56-other-org-metric')?.value as string)
    : getValueListName(utrv, code);

  const value = valueColumn.value;
  if (typeof value === 'string' && tableColumn.type === TableColumnType.Number) {
    try {
      return {
        code,
        intensity: formatCalculationNumber(value),
        name: name ?? '',
        numericValue: parseFloat(value)
      };
    } catch (e) {
      console.error(e)
    }
  }

  if (typeof value === 'number') {
    return {
      code,
      intensity: formatCalculationNumber(value),
      name: name ?? '',
      numericValue: value
    };
  }

  return { code, intensity: '', name: name ?? '', numericValue: 0 };
};
